import {
  forwardRef,
  type ChangeEvent,
  type ChangeEventHandler,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';
import { T } from '@territory/tokens';
import { isWedgeOnboardPrepFieldVariant, wedgeOnboardPrepFieldStyle, type WedgeOnboardPrepFieldVariant } from '../internal/wedgeOnboardPrepButton.js';
import { WEDGE_TIME_FIELD_CSS, wedgeTimeFieldStyle, wedgeTimeHitStyle } from '../internal/wedgeTimeField.js';
import { REMIX_FIELD_CSS, remixFieldStyle, type RemixFieldVariant } from '../internal/remixField.js';
import { font, radius, space, weight } from '../scale.js';
import { marketingDispatchFieldStyle } from '../internal/marketingDispatchField.js';
import { marketingResourceFieldStyle } from '../internal/marketingResourceField.js';
import { WEDGE_AUTH_FIELD_CSS, wedgeAuthFieldStyle, type WedgeAuthFieldVariant } from '../internal/wedgeAuthField.js';
import { wedgePhoneFieldStyle, type WedgePhoneFieldVariant } from '../internal/wedgePhoneField.js';
import { isWedgeFormFieldVariant, wedgeFormFieldStyle, type WedgeFormFieldVariant } from '../internal/wedgeFormField.js';
import { isWedgeTextareaVariant, wedgeTextareaFieldStyle, type WedgeTextareaVariant } from '../internal/wedgeTextareaField.js';
import { wedgeBatchFieldStyle } from '../internal/wedgeBatchButton.js';
import { isWedgeFindAccountsFieldVariant, wedgeFindAccountsFieldStyle } from '../internal/wedgeFindAccountsButton.js';
import { isWedgeOnboardSellFieldVariant, wedgeOnboardSellFieldStyle, type WedgeOnboardSellFieldVariant } from '../internal/wedgeOnboardSellButton.js';
import { isWedgeOnboardAreaVariant, wedgeOnboardAreaStyle, type WedgeOnboardAreaVariant } from '../internal/wedgeOnboardArea.js';
import { isWedgeBv2OutreachAreaVariant, wedgeBv2OutreachAreaStyle, type WedgeBv2OutreachAreaVariant } from '../internal/wedgeBv2OutreachButton.js';
import { isWedgeBriefLogAreaVariant, wedgeBriefLogAreaStyle, type WedgeBriefLogAreaVariant } from '../internal/wedgeBriefLogButton.js';
import { isWedgeTodayFieldVariant, wedgeTodayFieldStyle, type WedgeTodayFieldVariant } from '../internal/wedgeTodaySheetButton.js';
import { isWedgeJournalAreaVariant, wedgeJournalAreaStyle, type WedgeJournalAreaVariant } from '../internal/wedgeJournalArea.js';
import { isWedgeSheetAreaVariant, wedgeSheetAreaStyle, type WedgeSheetAreaVariant } from '../internal/wedgeMeetingButton.js';
import { isWedgeJournalEditVariant, wedgeJournalEditStyle, type WedgeJournalEditVariant } from '../internal/wedgeJournalEdit.js';
import { isWedgeNotesSearchVariant, wedgeNotesSearchStyle, type WedgeNotesSearchVariant } from '../internal/wedgeNotesListButton.js';
import { isWedgeNotesFieldVariant, wedgeNotesFieldStyle, type WedgeNotesFieldVariant } from '../internal/wedgeNotesEditorButton.js';
import { isWedgeNotesAreaVariant, wedgeNotesAreaStyle, type WedgeNotesAreaVariant } from '../internal/wedgeNotesArea.js';
import { isWedgePadAreaVariant, wedgePadAreaStyle, type WedgePadAreaVariant } from '../internal/wedgePadArea.js';
import { wedgeTerritoryFieldStyle, type WedgeTerritoryFieldVariant } from '../internal/wedgeTerritoryField.js';
import { isWedgeSavedFolderFieldVariant, wedgeSavedFolderFieldStyle, type WedgeSavedFolderFieldVariant } from '../internal/wedgeSavedFolderButton.js';
import { isWedgeLeadsFieldVariant, wedgeLeadsFieldStyle, type WedgeLeadsFieldVariant } from '../internal/wedgeLeadsButton.js';
import { isWedgeLeadsReviewFieldVariant, wedgeLeadsReviewFieldStyle, type WedgeLeadsReviewFieldVariant } from '../internal/wedgeLeadsReviewButton.js';
import { isWedgeLeadsImportAreaVariant, wedgeLeadsImportAreaStyle, type WedgeLeadsImportAreaVariant } from '../internal/wedgeLeadsImportButton.js';
import { isWedgeOnboardChipsFieldVariant, wedgeOnboardChipsFieldStyle, type WedgeOnboardChipsFieldVariant } from '../internal/wedgeOnboardChipsButton.js';
import { isWedgeOnboardTerritoryFieldVariant, wedgeOnboardTerritoryFieldStyle, type WedgeOnboardTerritoryFieldVariant } from '../internal/wedgeOnboardTerritory.js';
import { isWedgeOnboardTerritoryZipVariant, wedgeOnboardTerritoryZipStyle, type WedgeOnboardTerritoryZipVariant } from '../internal/wedgeOnboardTerritoryShape.js';
import { isWedgeOnboardIndustryRequestFieldVariant, wedgeOnboardIndustryRequestFieldStyle, type WedgeOnboardIndustryRequestFieldVariant } from '../internal/wedgeOnboardIndustryRequest.js';
import { isWedgeSearchConfirmFieldVariant, wedgeSearchConfirmFieldStyle, type WedgeSearchConfirmFieldVariant } from '../internal/wedgeSearchConfirmButton.js';
import { isWedgeRouteAnchorFieldVariant, wedgeRouteAnchorFieldStyle, type WedgeRouteAnchorFieldVariant } from '../internal/wedgeRouteAnchorsButton.js';
import { isWedgePipelineFieldVariant, wedgePipelineFieldStyle, type WedgePipelineFieldVariant } from '../internal/wedgePipelineButton.js';
import { isWedgeSavedTrayFieldVariant, wedgeSavedTrayFieldStyle, type WedgeSavedTrayFieldVariant } from '../internal/wedgeSavedTrayButton.js';
import { isWedgeOutreachDraftAreaVariant, isWedgeOutreachDraftFieldVariant, wedgeOutreachDraftAreaStyle, wedgeOutreachDraftFieldStyle, type WedgeOutreachDraftAreaVariant, type WedgeOutreachDraftFieldVariant } from '../internal/wedgeOutreachDraftButton.js';
import { isWedgeOutreachAreaVariant, isWedgeOutreachFieldVariant, wedgeOutreachAreaStyle, wedgeOutreachFieldStyle, type WedgeOutreachAreaVariant, type WedgeOutreachFieldVariant } from '../internal/wedgeOutreachButton.js';
import { isWedgeBv2FindFieldVariant, isWedgeBv2TakeFieldVariant, wedgeBv2FindFieldStyle, wedgeBv2TakeFieldStyle, type WedgeBv2FindFieldVariant, type WedgeBv2TakeFieldVariant } from '../internal/wedgeBv2TakeButton.js';
import { isWedgeBlipInputVariant, wedgeBlipInputStyle, type WedgeBlipInputVariant } from '../internal/wedgeBlipSheetButton.js';
import { isWedgeTeachFieldVariant, isWedgeTeachTitleVariant, wedgeTeachFieldStyle, wedgeTeachTitleStyle, type WedgeTeachFieldVariant, type WedgeTeachTitleVariant } from '../internal/wedgeTeachButton.js';
import { RECON_HOVER_MS } from '../internal/reconRoles.js';
import { consoleRole } from '../internal/consoleRoles.js';
import { useStyleOnce } from '../internal/useStyleOnce.js';
import {
  ADORN,
  ADORN_PAD,
  FIELD_CONSOLE_CSS,
  FIELD_CSS,
  FIELD_INLINE_CSS,
  FieldLabel,
  FieldMessage,
  consoleControlStyle,
  consoleInlineControlStyle,
  panelControlStyle,
  controlStyle,
  describedBy,
  renderIconSlot,
  rootStyle,
  useFieldIds,
  type FieldVariant,
  type IconSlot,
} from './field.js';

interface TextFieldBaseProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'size' | 'onChange' | 'value' | 'defaultValue'> {
  /* Visible label; use hideLabel for search boxes. */
  label?: ReactNode;
  hideLabel?: boolean;
  hint?: ReactNode;
  /* Sets aria-invalid and replaces the hint. */
  error?: ReactNode;
  required?: boolean;
  /* A number reaches the native input as it is, so React keeps the text of a number input that parses to the same value (a typed `1.0` or `1e3`). */
  value?: string | number;
  defaultValue?: string;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onValueChange?: (value: string) => void;
  /* Renders a textarea. */
  multiline?: boolean;
  rows?: number;
  size?: 'sm' | 'md';
  leading?: IconSlot;
  /* Clear button, valid check, unit. */
  trailing?: ReactNode;
  /* T.mono text, the consoles terminal look. */
  mono?: boolean;
  /* 'inline' (variant console only): the label is a run of the surrounding text and the field follows it on the same line, at
     its own width (legacy `.mbody .df`: a bare <label> then `input.finput`, the Rep Remove dialog). Without a visible label
     it changes nothing. 'docform' (variant console only): the label as the legacy admin `.docform label` (mono 10.5px, uppercase,
     tertiary ink, 5px under; the size follows the docform-label-size role), the Call a play form. */
  labelLook?: 'inline' | 'docform';
  /* 'note' (variant console, multiline, size md only): the legacy admin `.brannot textarea`, the manager note field of the Brief
     reader. The .finput in the body face at 1.45 leading with only the 36px field minimum, so `rows` sets the height, drawn
     inline-block on the baseline as the browser draws a textarea. Without the console variant, `multiline` or with size sm it
     changes nothing. */
  multilineLook?: 'note';
  /* 'end' (variant console only): the typed text sits at the right edge of the field (legacy `.qedrow input`, a number in the
     admin quota editor row). Without the console variant it changes nothing. */
  align?: 'end';
  /* 'console': the owner console field (legacy .finput): 36px, mono 13px, console field roles. `size` is ignored, except
     size="sm" draws the 32px journal field.
     'console-inline': the owner Enterprise inline field (legacy `IS`); `size` and `mono` are ignored.
     'wedge-prep-time' and 'wedge-prep-time-on': the onboarding prep time input (native type time, 16px type); the on look draws the accent border once the typed time is the chosen one.
     'wedge-time': the NotifPrefs native time input with a transparent 44px label hit target.
     'wedge-plan-pick': the date and time inputs of the Plan event sheet Reschedule form, one bare native input each; the caller passes the whole look.
     'wedge-phone' and 'wedge-phone-code': the phone and 6-digit code inputs of the Settings text-briefing screen, one bare native input each.
     'wedge-form-field', 'wedge-sheet-field', 'wedge-add-field' (and '-lg', '-sm'), 'wedge-answer-field', 'wedge-settings-field', 'wedge-search-field',
     'wedge-address-field', 'wedge-onboard-field', 'wedge-folder-field', 'wedge-folder-side-field', 'wedge-confirm-field', 'wedge-domain-field',
     'wedge-bare-field', 'wedge-deal-field', 'wedge-deal-name-field', 'wedge-saved-search-field', 'wedge-search-bar-field', 'wedge-command-field', 'wedge-brief-find-field' (and '-open') and 'wedge-business-field': the released one-line Wedge text fields, one bare native input each (catalog: Wedge form field looks).
     'wedge-onboard-area': the about-you textarea of the onboarding identity step, one bare native textarea (the large onboarding field with 14px padding, a 1.5 line height, no resize and a 130px floor). `rows` is required.
     'wedge-sell-area' and 'wedge-sell-field': the pitch textarea and the company input of the onboarding sell step, one bare native element each (14px padding and a 14px radius with 1.45 line height; 13px by 14px padding and a 13px radius). `rows` is required on the area.
     'wedge-chips-field' and 'wedge-chips-field-full': the competitor name input of the onboarding Territory step, one bare native input each (flex line, 11px by 13px padding); the full look is the half opacity of a full list.
     'wedge-answer-area', 'wedge-refinement-area', 'wedge-settings-area', 'wedge-note-area', 'wedge-notecard-area' and 'wedge-outcome-area': the released multi-line Wedge text fields, one bare native textarea each
     (catalog: Wedge textarea looks). `rows` is required: every shipped site sets its own.
     'wedge-sheet-area': the notes field of the Schedule meeting sheet, the textarea of `wedge-sheet-field` with a vertical resize handle (catalog: Wedge textarea looks).
     'wedge-journal-area' and 'wedge-journal-area-mobile': the composer textarea of the journal (desktop and phone). The caller says whether it has focus and
     whether the theme is light (`journalFocused`, `journalLight`); the look paints the border, the ring and the rest shadow from them.
     'wedge-journal-edit' and 'wedge-journal-edit-mobile': the inline editor of a journal entry (desktop and phone), the Saved row note editor in the type of the entry
     body. `rows` is required; focus is on mount and the caller owns it.
     'wedge-brief-log-area': the note field of the brief log tab, one bare native textarea (block, full width, 96px tall, 8px above, 14px padding, 14px radius, 15px type, no resize). The caller passes the border, the fill, the ink and the font family. `rows` is required.
     'wedge-notes-area': the transcript textarea of the Notes app dictation sheet, one bare native textarea. `rows` is optional and the legacy textarea prints none; the class `rvn-transcript` keeps the border, the radius and the focus state.
     'wedge-notes-search': the search input of the Notes app list, one bare native input that fills its pill (flex 1, no fill, 16px ink of the page); the class `rvn-search input` keeps the border, the outline and the placeholder.
     'wedge-bv2-lineup-field': the add field of the brief v2 lineup editor, one bare native input (42px high, 14px side padding, 12px radius, 1px border, card fill, 16px type). The caller sets the font family and the brief color roles.
     'wedge-bv2-find': the find field of the brief v2 header, one bare native input that grows inside its label pill (14px type, no border, no fill, no outline). The caller sets the font family and the label draws the pill.
     'wedge-teach-paste-area': the paste textarea of the Teach guide, one bare native textarea (10px padding, 10px radius, 1px card border, elevated fill, 13px type, vertical resize). `rows` is required.
     'wedge-blip-input' and 'wedge-blip-input-off': the box at the foot of the Blip sheet, one bare native textarea (flex 1, no resize, 96px cap, 10px by 12px padding, 14px radius, 1px card border, surface fill, 16px type in the Wedge font, no outline). The off variant dims it to 60% while the mic listens. `rows` is required.
     'wedge-saved-tray-field': the tag input of the Saved tag tray, one bare native input (full width, 56px of right padding for the counter over it, 11px radius,
     the elevated fill, a 1px card border, 16px type).
     'wedge-teach-row-area' and 'wedge-teach-row-area-add': the body textarea of a Teach row in edit mode (elevated fill) and in the add form (surface fill), 7px by 9px padding, 8px radius, 12.5px type. `rows` is required.
     'wedge-teach-row-title' and 'wedge-teach-row-title-add': the title input of a Teach row in edit mode (elevated fill) and in the add form (surface fill), one bare native input, 13px bold type, 6px margin below.
     the elevated fill, a 1px card border, 16px type). 'wedge-saved-tray-note': the note input of the Saved handoff picker, one bare native input (full width,
     10px above, 11px by 12px padding, 11px radius, the surface fill, a 1px card border, 13.5px type).
     'wedge-lead-field' and 'wedge-lead-value': the text fields of the Pipeline add-lead form, one bare native input each (44px tall at least, border-box, full width, 9px radius, 14px type; the value field sets no family and no outline). Pass `type` and `inputMode` through.
     'wedge-pipeline-search': the search input of the Pipeline board header, one bare native input (44px high, full width, 12px radius, 36px of left padding for
     the glass, 15px type). The caller passes the fill, border and ink from its own palette.
     'wedge-pipeline-name': the saved view name input of the Pipeline filter sheet, one bare native input (48px high, border-box, grows, 14px radius, 15px type).
     The caller passes the fill, border and ink.
     'wedge-find-account-field': the ACCOUNT TYPE input of the Find accounts card, one bare native input (50px tall, border-box, full width, 0 by 14px padding, 14px radius, 16px type). The caller passes the fill, the border, the ink and the font family in style.
     'wedge-find-zip-field': the ZIP input of the Find accounts Step 02, the same box 104px wide with 17px type and 1px letter spacing.
     'wedge-territory': the primary territory input of the Settings territory screen, one bare native input.
     'wedge-outreach-note-area': the lead note editor of an Outreach card, one bare native textarea (full width, 1px accent border, 10px radius, the surface fill, 13px type, vertical resize). `rows` is required.
     'wedge-outreach-log-area': the touch note of the Outreach More sheet, one bare native textarea (full width, border-box, 1px card border, 12px radius, the surface fill, 15px type, 10px by 12px padding, vertical resize). `rows` is required.
     'wedge-outreach-date-field': the follow-up date input of the Outreach More sheet, one bare native input (flex 1, 44px minimum height, no box sizing, 1px card border, 12px radius, the surface fill, 15px type, 0 by 10px padding).
     'wedge-outreach-draft-subject': the Subject input of the Outreach draft sheet, one bare native input (full width, border-box, 1px card border, 12px radius, the surface fill, 15px type at weight 600, 10px by 12px padding, no focus ring, 10px bottom margin).
     'wedge-outreach-draft-text-area' and 'wedge-outreach-draft-email-area': the message body of the Outreach draft sheet, one bare native textarea (the subject box with 1.45 leading and a vertical resize handle; 120px minimum height for the text, 220px for the email). `rows` is required.
     'wedge-outreach-phone-field': the phone input of the Outreach add phone row, one bare native input (flex 1, 44px tall, 1px accent border, 12px radius, the surface fill, 14px type).
     'wedge-saved-folder-rename-field' and 'wedge-saved-folder-new-field': the rename and new-folder inputs of the Saved move-to-folder sheet, one bare native input each
     (16px type, flex 1, min width 0; rename takes the 0.5px accent border, new folder the 1px card border).
     The caller owns naming, constraints and validation. Use external htmlFor labels or ARIA; do not enclose it in a label.
     'recon': the shipped Recon .text-input, a bare 54px input with no wrapper, label, hint or message of its own (the
     app lays out the visible label). `multiline` renders the shipped day-note textarea. `label` names the input (aria-label) unless aria-labelledby is set; `className`,
     `style` and the ref land on the input; `error` sets aria-invalid, painted amber; `leading`, `trailing`,
     `size` and `mono` are ignored. The look is a first-injected sheet at the shipped specificity. */
  variant?: FieldVariant | 'recon' | WedgeOnboardSellFieldVariant | 'wedge-time' | 'wedge-plan-pick' | WedgeOnboardPrepFieldVariant | WedgeAuthFieldVariant | WedgePhoneFieldVariant | WedgeTerritoryFieldVariant | WedgeFormFieldVariant | WedgeTextareaVariant | WedgeSheetAreaVariant | WedgeTodayFieldVariant | WedgeJournalAreaVariant | WedgeJournalEditVariant | 'wedge-batch-field' | 'wedge-find-account-field' | 'wedge-find-zip-field' | WedgeNotesFieldVariant | WedgeNotesAreaVariant | WedgePadAreaVariant | WedgeSavedFolderFieldVariant | WedgeSavedTrayFieldVariant | WedgePipelineFieldVariant | WedgeTeachFieldVariant | WedgeTeachTitleVariant | WedgeBlipInputVariant | WedgeOutreachAreaVariant | WedgeOutreachFieldVariant | WedgeOutreachDraftAreaVariant | WedgeOutreachDraftFieldVariant | 'marketing-resource' | 'marketing-dispatch' | RemixFieldVariant | WedgeNotesSearchVariant | WedgeLeadsFieldVariant | WedgeLeadsImportAreaVariant | WedgeLeadsReviewFieldVariant | WedgeBriefLogAreaVariant | WedgeBv2OutreachAreaVariant | WedgeBv2TakeFieldVariant | WedgeBv2FindFieldVariant | WedgeOnboardAreaVariant | WedgeOnboardChipsFieldVariant | WedgeOnboardIndustryRequestFieldVariant | WedgeOnboardTerritoryFieldVariant | WedgeOnboardTerritoryZipVariant | WedgeRouteAnchorFieldVariant | WedgeSearchConfirmFieldVariant;
  /* Marketing dispatch retains caller focus handlers, labels and messages. */
  dispatchFocused?: boolean;
  /* Exact brandField visual state. The caller retains native focus and blur handlers. Ignored by other variants. */
  authFocused?: boolean;
  /* Native auth only. Touch adds the released Reset minimum height; other geometry stays unchanged. */
  authSize?: 'touch' | 'recovery';
  /* Shared auth only. PIN callers retain their flat focus paint without the brandField shadow. */
  authFocus?: 'flat';
  /* Shared auth only. Grant fields retain the browser's initial transition values. */
  authMotion?: 'instant';
  /* Account only. Retains the caller's native password visibility inset. */
  authInset?: 'password';
  /* Landing account/magic only. Visual border state; sets no native or ARIA validation state. */
  authBorder?: 'warning';
  /* 'wedge-form-field' only. Paints the released amber invalid border. Sets no native or ARIA validation state; the caller owns aria-invalid. */
  wedgeBorder?: 'invalid';
  /* The journal composer areas only. Paints the accent border and the 4px accent tint ring. The caller keeps native focus and blur handlers. */
  journalFocused?: boolean;
  /* The journal composer areas only. True in the light and sun themes, where the field at rest casts the hairline journal shadow. */
  journalLight?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-testid'?: string;
}

/* Existing variants require a label. Explicit native variants retain caller labels or ARIA. */
export type TextFieldProps = TextFieldBaseProps & (
  | { variant: 'wedge-time'; type?: 'time'; multiline?: false; label?: ReactNode }
  | { variant: 'wedge-plan-pick'; type: 'date' | 'time'; multiline?: false; label?: ReactNode }
  | { variant: WedgeOnboardPrepFieldVariant; type: 'time'; multiline?: false; label?: ReactNode }
  | { variant: WedgeAuthFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgePhoneFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeFormFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeTextareaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeSheetAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeJournalAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeJournalEditVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeNotesFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeNotesAreaVariant; multiline?: true; rows?: number; label?: ReactNode }
  | { variant: WedgePadAreaVariant; multiline?: true; rows?: number; label?: ReactNode }
  | { variant: WedgeLeadsImportAreaVariant; multiline?: true; rows?: number; label?: ReactNode }
  | { variant: WedgeBriefLogAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeBv2OutreachAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeRouteAnchorFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeSearchConfirmFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOnboardChipsFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOnboardIndustryRequestFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOnboardTerritoryFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOnboardTerritoryZipVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOnboardAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: 'wedge-sell-area'; multiline?: true; rows: number; label?: ReactNode }
  | { variant: 'wedge-sell-field'; multiline?: false; label?: ReactNode }
  | { variant: WedgeLeadsReviewFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeTerritoryFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeSavedFolderFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeSavedTrayFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgePipelineFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeLeadsFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: 'wedge-batch-field'; multiline?: false; label?: ReactNode }
  | { variant: 'wedge-find-account-field' | 'wedge-find-zip-field'; multiline?: false; label?: ReactNode }
  | { variant: WedgeTeachFieldVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeBlipInputVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeTeachTitleVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeBv2TakeFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeBv2FindFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOutreachAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeTodayFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOutreachFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeOutreachDraftAreaVariant; multiline?: true; rows: number; label?: ReactNode }
  | { variant: WedgeOutreachDraftFieldVariant; multiline?: false; label?: ReactNode }
  | { variant: WedgeNotesSearchVariant; multiline?: false; label?: ReactNode }
  | { variant: 'marketing-resource'; multiline?: false; label?: ReactNode }
  | { variant: 'marketing-dispatch'; label?: ReactNode }
  | { variant: RemixFieldVariant; multiline?: false; label?: ReactNode }
  | { variant?: FieldVariant | 'recon'; label: ReactNode }
);

/* The ref is the input (or textarea), which is what forms focus and read; style and className land on the root. */
/* Recon .text-input, one for one, at class specificity (it must beat the global `input { font: inherit }`). The sheet
   goes in first, so Recon layout classes (.filters .text-input, .stepper input) on the same element still win. */
const RC = '[data-rv-recon-field]';
const RECON_FIELD_CSS = `${RC} { width: 100%; min-height: 54px; padding: 0 ${space[4]}px; border-radius: ${radius.lg}px; border: 1px solid ${T.separatorBold}; background: ${T.surface}; font-size: ${font.title3}px; font-weight: ${weight.medium}; transition: border-color ${RECON_HOVER_MS}ms ${T.ease}, box-shadow ${RECON_HOVER_MS}ms ${T.ease}; }
:where([data-rv-recon-field])[data-rv-multiline] { display: block; min-height: 96px; padding: ${space[3]}px ${space[4]}px; resize: vertical; line-height: 1.4; }
${RC}::placeholder { color: ${T.labelTer}; font-weight: ${weight.regular}; }
${RC}:focus { outline: none; border-color: ${T.accent}; box-shadow: 0 0 0 4px ${T.accentSoft}; }
${RC}[aria-invalid="true"] { border-color: ${T.amber}; box-shadow: 0 0 0 4px ${T.amberSoft}; }`;

export const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(function TextField(props, ref) {
  const {
    label,
    hideLabel,
    hint,
    error,
    required,
    onChange,
    onValueChange,
    multiline,
    rows = 3,
    size = 'md',
    leading,
    trailing,
    mono,
    labelLook,
    multilineLook,
    align,
    variant = 'default',
    dispatchFocused = false,
    authFocused = false,
    authSize,
    authFocus,
    authMotion,
    authInset,
    authBorder,
    wedgeBorder,
    journalFocused = false,
    journalLight = false,
    id,
    disabled,
    className,
    style,
    'data-testid': testId,
    'aria-describedby': ownDescribedBy,
    ...rest
  } = props;
  useStyleOnce('remix-field', REMIX_FIELD_CSS);
  useStyleOnce('field', FIELD_CSS);
  useStyleOnce('field-console', FIELD_CONSOLE_CSS);
  useStyleOnce('field-inline', FIELD_INLINE_CSS);
  useStyleOnce('field-recon', RECON_FIELD_CSS, { first: true });
  useStyleOnce('field-wedge-auth', WEDGE_AUTH_FIELD_CSS);
  useStyleOnce('field-wedge-time', WEDGE_TIME_FIELD_CSS);
  const ids = useFieldIds(id);
  const panelLook = variant === 'panel';
  const consoleLook = variant === 'console' || panelLook;
  const compact = variant === 'console' && size === 'sm';
  const inlineLook = variant === 'console-inline';
  const inlineLabel = labelLook === 'inline' && variant === 'console' && !hideLabel;
  const docformLabel = labelLook === 'docform' && variant === 'console' && !hideLabel;
  const invalid = Boolean(error);
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange?.(e);
    onValueChange?.(e.target.value);
  };
  if (variant === 'wedge-plan-pick') {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={style} />;
  }
  if (isWedgeOnboardPrepFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required} data-rv-wedge-field={variant}
      data-testid={testId} className={className} aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardPrepFieldStyle(variant), ...style }} />;
  }
  if (variant === 'wedge-time') {
    return <label data-rv-wedge-time-hit="" style={wedgeTimeHitStyle}><input {...rest} type="time" ref={ref as Ref<HTMLInputElement>}
      id={id} disabled={disabled} required={required} data-testid={testId}
      data-rv-wedge-time="" className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange}
      style={{...wedgeTimeFieldStyle, ...style}} /></label>;
  }
  if (variant === 'wedge-auth' || variant === 'wedge-auth-recipient' || variant === 'wedge-auth-pin' || variant === 'wedge-auth-account' || variant === 'wedge-auth-magic') {
    return (
      <input
        {...rest}
        ref={ref as Ref<HTMLInputElement>}
        id={id}
        data-rv-auth-field={variant}
        data-testid={testId}
        className={className}
        style={{ ...wedgeAuthFieldStyle(variant, authFocused, {size: authSize, focus: authFocus, motion: authMotion, inset: authInset, border: authBorder}), ...style }}
        disabled={disabled}
        required={required}
        aria-describedby={ownDescribedBy}
        onChange={handleChange}
      />
    );
  }
  if (variant === 'wedge-phone' || variant === 'wedge-phone-code') {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-phone={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgePhoneFieldStyle(variant), ...style }} />;
  }
  if (variant === 'wedge-territory') {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-territory="" data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeTerritoryFieldStyle(), ...style }} />;
  }
  if (variant === 'wedge-batch-field') {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeBatchFieldStyle(), ...style }} />;
  }
  if (isWedgeFindAccountsFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeFindAccountsFieldStyle(variant), ...style }} />;
  }
  if (isWedgeFormFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeFormFieldStyle(variant, wedgeBorder === 'invalid'), ...style }} />;
  }
  if (isWedgeSheetAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeSheetAreaStyle(variant), ...style }} />;
  }
  if (isWedgeTodayFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeTodayFieldStyle(variant), ...style }} />;
  }
  if (isWedgeSavedTrayFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeSavedTrayFieldStyle(variant), ...style }} />;
  }
  if (isWedgeLeadsFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeLeadsFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOnboardChipsFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardChipsFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOnboardIndustryRequestFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardIndustryRequestFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOnboardTerritoryFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardTerritoryFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOnboardTerritoryZipVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardTerritoryZipStyle(variant), ...style }} />;
  }
  if (isWedgeSearchConfirmFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeSearchConfirmFieldStyle(), ...style }} />;
  }
  if (isWedgeRouteAnchorFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeRouteAnchorFieldStyle(), ...style }} />;
  }
  if (isWedgePipelineFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgePipelineFieldStyle(variant), ...style }} />;
  }
  if (isWedgeSavedFolderFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeSavedFolderFieldStyle(variant), ...style }} />;
  }
  if (isWedgeJournalAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeJournalAreaStyle(variant, journalFocused, journalLight), ...style }} />;
  }
  if (isWedgeJournalEditVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeJournalEditStyle(variant), ...style }} />;
  }
  if (isWedgeOutreachDraftFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOutreachDraftFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOutreachDraftAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOutreachDraftAreaStyle(variant), ...style }} />;
  }
  if (isWedgeOutreachFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOutreachFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOutreachAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOutreachAreaStyle(variant), ...style }} />;
  }
  if (isWedgeNotesSearchVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeNotesSearchStyle(), ...style }} />;
  }
  if (isWedgeBv2TakeFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeBv2TakeFieldStyle(), ...style }} />;
  }
  if (isWedgeBv2FindFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeBv2FindFieldStyle(), ...style }} />;
  }
  if (isWedgeTeachTitleVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeTeachTitleStyle(variant), ...style }} />;
  }
  if (isWedgeBlipInputVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeBlipInputStyle(variant), ...style }} />;
  }
  if (isWedgeTeachFieldVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeTeachFieldStyle(variant), ...style }} />;
  }
  if (isWedgeNotesFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeNotesFieldStyle(), ...style }} />;
  }
  if (isWedgeLeadsReviewFieldVariant(variant)) {
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeLeadsReviewFieldStyle(variant), ...style }} />;
  }
  if (isWedgeLeadsImportAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeLeadsImportAreaStyle(variant), ...style }} />;
  }
  if (isWedgeOnboardSellFieldVariant(variant)) {
    if (variant === 'wedge-sell-area') {
      return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
        id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
        aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardSellFieldStyle(variant), ...style }} />;
    }
    return <input {...rest} ref={ref as Ref<HTMLInputElement>} id={id} disabled={disabled} required={required}
      data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardSellFieldStyle(variant), ...style }} />;
  }
  if (isWedgeOnboardAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeOnboardAreaStyle(variant), ...style }} />;
  }
  if (isWedgeBv2OutreachAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeBv2OutreachAreaStyle(variant), ...style }} />;
  }
  if (isWedgeBriefLogAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeBriefLogAreaStyle(variant), ...style }} />;
  }
  if (isWedgeNotesAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeNotesAreaStyle(), ...style }} />;
  }
  if (isWedgePadAreaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={props.rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgePadAreaStyle(variant), ...style }} />;
  }
  if (isWedgeTextareaVariant(variant)) {
    return <textarea {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} rows={rows}
      id={id} disabled={disabled} required={required} data-rv-wedge-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...wedgeTextareaFieldStyle(variant), ...style }} />;
  }
  if (variant === 'remix' || variant === 'remix-editor' || variant === 'remix-url' || variant === 'remix-number') {
    return <input ref={ref as Ref<HTMLInputElement>} {...rest} id={id} disabled={disabled} required={required}
      data-rv-remix-field={variant} data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...remixFieldStyle(variant), ...style }} />;
  }
  if (variant === 'marketing-resource') {
    return <input ref={ref as Ref<HTMLInputElement>} {...rest} id={id} disabled={disabled} required={required}
      data-rv-marketing-field="resource" data-testid={testId} className={className}
      aria-describedby={ownDescribedBy} onChange={handleChange} style={{ ...marketingResourceFieldStyle(), ...style }} />;
  }
  if (variant === 'marketing-dispatch') {
    const shared = {
      id, disabled, required, className, 'data-testid': testId,
      'data-rv-marketing-field': 'dispatch',
      'aria-describedby': ownDescribedBy,
      onChange: handleChange,
      /* The shipped wrapper lets a caller replace the entire style object. */
      style: Object.prototype.hasOwnProperty.call(props, 'style') ? style : marketingDispatchFieldStyle(dispatchFocused, invalid, Boolean(multiline), rows),
    };
    return multiline
      ? <textarea ref={ref as Ref<HTMLTextAreaElement>} rows={rows} {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} {...shared} />
      : <input ref={ref as Ref<HTMLInputElement>} {...rest} {...shared} />;
  }
  if (variant === 'recon') {
    const named = rest['aria-labelledby'] === undefined && typeof label === 'string' ? label : undefined;
    const sharedRecon = {
      ...rest,
      id,
      'data-rv-recon-field': '',
      'data-testid': testId,
      className,
      style,
      disabled,
      required,
      'aria-label': named,
      'aria-invalid': invalid || undefined,
      'aria-describedby': ownDescribedBy,
      onChange: handleChange,
    } as const;
    return multiline ? (
      <textarea {...(sharedRecon as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)} ref={ref as Ref<HTMLTextAreaElement>} data-rv-multiline="" rows={rows} />
    ) : (
      <input type="text" {...sharedRecon} ref={ref as Ref<HTMLInputElement>} />
    );
  }
  const lead = renderIconSlot(leading, ADORN);
  const base = inlineLook
    ? consoleInlineControlStyle({ error: invalid, multiline })
    : panelLook
    ? panelControlStyle({ error: invalid, multiline })
    : consoleLook
    ? consoleControlStyle({ error: invalid, multiline, compact, inline: inlineLabel, note: variant === 'console' && multilineLook === 'note' })
    : controlStyle({ size, error: invalid, multiline, mono });
  const inputStyle: CSSProperties = {
    ...base,
    ...(disabled && variant === 'console' ? { cursor: consoleRole('field-disabled-cursor', 'not-allowed') } : null),
    /* Spread only when set: an undefined longhand after the padding shorthand clears it in React. */
    ...(align === 'end' && variant === 'console' ? { textAlign: 'right' as const } : null),
    ...(lead ? { paddingLeft: ADORN_PAD } : null),
    ...(trailing ? { paddingRight: space[10] } : null),
  };
  const shared = {
    id: ids.id,
    'data-rv-field-control': '',
    'data-rv-look': inlineLook ? 'console-inline' : consoleLook ? 'console' : undefined,
    disabled,
    required,
    'aria-required': required || undefined,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy(ids, ownDescribedBy, hint, error),
    onChange: handleChange,
    style: inputStyle,
  };
  const adornBox: CSSProperties = {
    position: 'absolute',
    top: multiline ? space[3] : 0,
    bottom: multiline ? undefined : 0,
    display: 'flex',
    alignItems: 'center',
    color: T.labelTer,
  };

  return (
    <div data-rv-textfield="" data-testid={testId} className={className} data-rv-disabled={disabled ? '' : undefined} style={{ ...rootStyle(false, style, inlineLabel), ...(disabled ? { ['--rv-field-dim' as string]: variant === 'console' ? consoleRole('field-disabled-opacity', 0.4) : 0.4 } : null) }}>
      <FieldLabel htmlFor={ids.id} hidden={hideLabel} required={required} caps={panelLook} inline={inlineLabel} docform={docformLabel}>
        {label}
      </FieldLabel>
      <div style={inlineLabel ? { position: 'relative', display: 'inline' } : { position: 'relative' }}>
        {multiline ? (
          <textarea
            ref={ref as Ref<HTMLTextAreaElement>}
            rows={rows}
            {...(rest as unknown as TextareaHTMLAttributes<HTMLTextAreaElement>)}
            {...shared}
          />
        ) : (
          <input ref={ref as Ref<HTMLInputElement>} type="text" {...rest} {...shared} />
        )}
        {lead ? (
          <span aria-hidden="true" style={{ ...adornBox, left: space[3], pointerEvents: 'none' }}>
            {lead}
          </span>
        ) : null}
        {trailing ? <span style={{ ...adornBox, right: space[3] }}>{trailing}</span> : null}
      </div>
      <FieldMessage ids={ids} hint={hint} error={error} caps={panelLook} />
    </div>
  );
});
